/* eslint-disable @typescript-eslint/no-use-before-define */
import { animated, to, useTransition } from '@react-spring/web'
import { Group } from '@visx/group'
import { ParentSize } from '@visx/responsive'
import { scaleOrdinal } from '@visx/scale'
import Pie, { PieArcDatum, ProvidedProps } from '@visx/shape/lib/shapes/Pie'
import { useState } from 'react'

const PIE_COLORS = [
  '#7FE7D7', // lighter teal for Cash on delivery
  '#A5A7FF', // lighter blue for EMI
  '#B8ECA4', // lighter green for Pay later
  '#FFB1E1', // lighter pink for Others
  '#FFB1AE', // lighter red for Split COD
  '#D4B1FF', // lighter purple for Card
  '#A8E7D7', // lighter teal variant for Wallet
  '#B1B3FF', // lighter blue variant for UPI
  '#C2FFB1' // lighter green variant for Netbanking
]

// Generic data type for pie chart
export interface PieChartData {
  label: string
  value: number | string
  change: number | string
}

interface PieChartProps {
  data: PieChartData[]
  colors?: string[]
  formatter: (value: number | string) => string
}

const defaultMargin = { top: 20, right: 20, bottom: 20, left: 20 }

export interface AnimatedPieProps extends PieChartProps {
  width: number
  height: number
  margin?: typeof defaultMargin
  animate?: boolean
}

function InternalPie({
  width,
  height,
  margin = defaultMargin,
  animate = true,
  data,
  colors = PIE_COLORS,
  formatter
}: AnimatedPieProps) {
  const [selectedSlice, setSelectedSlice] = useState<string | null>(null)

  if (width < 10) return null

  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom
  const radius = Math.min(innerWidth, innerHeight) / 2
  const centerY = innerHeight / 2
  const centerX = innerWidth / 2
  const donutThickness = 80

  // Create color scale
  const getSliceColor = scaleOrdinal({
    domain: data.map(d => d.label),
    range: colors
  })

  // Convert string values to numbers for the pie chart
  const getValue = (d: PieChartData) => (typeof d.value === 'string' ? parseFloat(d.value) : d.value)

  return (
    <svg width={width} height={height}>
      <rect rx={14} width={width} height={height} fill="url('#visx-pie-gradient')" />
      <Group top={centerY + margin.top} left={centerX + margin.left}>
        <Pie
          data={selectedSlice ? data.filter(({ label }) => label === selectedSlice) : data}
          pieValue={getValue}
          outerRadius={radius}
          innerRadius={radius - donutThickness}
          cornerRadius={6}
          padAngle={0.005}
        >
          {pie => (
            <AnimatedPieSlices<PieChartData>
              {...pie}
              animate={animate}
              getKey={arc => arc.data.label}
              onClickDatum={({ data: { label } }) =>
                animate && setSelectedSlice(selectedSlice && selectedSlice === label ? null : label)
              }
              getColor={arc => getSliceColor(arc.data.label)}
              formatter={formatter}
            />
          )}
        </Pie>
      </Group>
      {/* {animate && (
        <text
          textAnchor="end"
          x={width - 16}
          y={height - 16}
          fill="white"
          fontSize={11}
          fontWeight={300}
          pointerEvents="none"
        >
          Click segments to update
        </text>
      )} */}
    </svg>
  )
}

// react-spring transition definitions
type AnimatedStyles = { startAngle: number; endAngle: number; opacity: number }

const fromLeaveTransition = ({ endAngle }: PieArcDatum<any>) => ({
  startAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  endAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  opacity: 0
})

const enterUpdateTransition = ({ startAngle, endAngle }: PieArcDatum<any>) => ({
  startAngle,
  endAngle,
  opacity: 1
})

type AnimatedPieSliceProps<Datum> = ProvidedProps<Datum> & {
  animate?: boolean
  getKey: (d: PieArcDatum<Datum>) => string
  getColor: (d: PieArcDatum<Datum>) => string
  onClickDatum: (d: PieArcDatum<Datum>) => void
  formatter: (value: number | string) => string
  delay?: number
}

function AnimatedPieSlices<Datum extends PieChartData>({
  animate,
  arcs,
  path,
  getKey,
  getColor,
  onClickDatum,
  formatter
}: AnimatedPieSliceProps<Datum>) {
  const transitions = useTransition<PieArcDatum<Datum>, AnimatedStyles>(arcs, {
    from: animate ? fromLeaveTransition : enterUpdateTransition,
    enter: enterUpdateTransition,
    update: enterUpdateTransition,
    leave: animate ? fromLeaveTransition : enterUpdateTransition,
    keys: getKey
  })

  return transitions((props, arc, { key }) => {
    const [centroidX, centroidY] = path.centroid(arc)
    const hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.3

    return (
      <g key={key}>
        <animated.path
          d={to([props.startAngle, props.endAngle], (startAngle, endAngle) =>
            path({
              ...arc,
              startAngle,
              endAngle
            })
          )}
          fill={getColor(arc)}
          className={'cursor-pointer'}
          onClick={() => onClickDatum(arc)}
          onTouchStart={() => onClickDatum(arc)}
        />
        {hasSpaceForLabel && (
          <animated.g style={{ opacity: props.opacity }}>
            <foreignObject
              x={centroidX - 50}
              y={centroidY - 30}
              width={100}
              height={80}
              style={{
                overflow: 'visible',
                pointerEvents: 'none'
              }}
            >
              <div className='text-center text-[12px] font-semibold text-gray-800'>{arc.data.label}</div>
              <div className='text-center text-[10px] font-semibold mt-1 text-gray-700'>
                <span>{formatter(arc.data.value)}</span>

                {arc.data.change ? (
                  <span
                    className={`w-full text-center text-[10px] font-semibold mt-[2px] flex items-center justify-center gap-1 ${
                      typeof arc.data.change === 'number'
                        ? parseFloat(String(arc.data.change)) > 0
                          ? 'text-emerald-600'
                          : 'text-rose-600'
                        : 'text-gray-700'
                    }`}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        transform: `rotate(${parseFloat(String(arc.data.change)) > 0 ? 45 : -45}deg)`
                      }}
                    >
                      {typeof arc.data.change === 'number' || typeof arc.data.change === 'string'
                        ? parseFloat(String(arc.data.change)) > 0
                          ? '↑'
                          : '↓'
                        : ''}
                    </span>
                    <span>{formatter(arc.data.change)}</span>
                  </span>
                ) : null}
              </div>
            </foreignObject>
          </animated.g>
        )}
      </g>
    )
  })
}

export function PieChart({ data, colors = PIE_COLORS, formatter }: PieChartProps) {
  return (
    <ParentSize>
      {({ width, height }) => (
        <InternalPie width={width} height={height} data={data} colors={colors} formatter={formatter} />
      )}
    </ParentSize>
  )
}
